import queryString from "query-string";

import { BTLocalStorage } from "types/btStorage";

import { type OAuthType } from "entity/login/Login/Login.api.types";

/**
 * How long a stored OAuth handoff is honored. The round trip can include an Auth0
 * login, an MFA challenge and a password reset, so the window is generous — but it
 * is bounded so a user who abandons the flow is not locked out of route-level
 * redirects (notably the setup experience) indefinitely on that device.
 */
export const OAUTH_FLOW_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * True while an external OAuth handoff (integration partner, Skilljar, THD, ...) is
 * mid-flight. Route-level redirects must not interrupt such a flow: unmounting the
 * login screens before they can mint and forward an OAuth code strands the partner
 * without its `code` and `state`.
 *
 * Two signals are checked because neither covers the whole flow on its own:
 * - the query string, which carries the OAuth params on the first paint of
 *   `/app/Login` before any state has been persisted; and
 * - `bt-object-oauthRedirectState`, which carries it across the round trip to Auth0,
 *   where those params are gone.
 *
 * @param search Query string to inspect. Defaults to the current location's.
 */
export function isOAuthFlowInProgress(search: string = window.location.search): boolean {
    return hasOAuthQueryParams(search) || hasUnexpiredOAuthRedirectState();
}

function hasOAuthQueryParams(search: string): boolean {
    // eslint-disable-next-line deprecate/member-expression
    const qs = queryString.parse(search);

    return (
        typeof qs.oauthRedirectType === "string" ||
        qs.redirectskilljar === "true" ||
        qs.redirectdataproduct === "true"
    );
}

function hasUnexpiredOAuthRedirectState(): boolean {
    const oauthRedirectState = BTLocalStorage.get("bt-object-oauthRedirectState");
    if (!oauthRedirectState?.createdAt) {
        // Entries written before createdAt existed carry no age, so they fail closed
        // rather than suppressing redirects forever.
        return false;
    }

    return Date.now() - oauthRedirectState.createdAt < OAUTH_FLOW_MAX_AGE_MS;
}

/**
 * Persists an external OAuth handoff so it survives the round trip to Auth0, which drops the
 * partner's query params. The login screens read it back once the user returns signed in and
 * forward the minted OAuth code to `redirectUri`. Pass `returnSearch` to resume on
 * `/Login/Password` with that query string instead.
 */
export function storeOAuthRedirectState(
    oauthRedirectType: OAuthType | undefined,
    redirectUri: string | undefined,
    stateParam: string | undefined,
    returnSearch?: string,
): void {
    if (oauthRedirectType && redirectUri) {
        BTLocalStorage.set("bt-object-oauthRedirectState", {
            oauthRedirectType,
            redirectURI: redirectUri,
            state: stateParam ?? "",
            createdAt: Date.now(),
            ...(returnSearch ? { returnSearch } : {}),
        });
    }
}

/**
 * Drops `returnSearch` from the stored handoff so a resumed `/Login/Password` visit can't bounce
 * back to itself. Returns the search that was stored, if any.
 */
export function consumeOAuthReturnSearch(): string | undefined {
    const oauthRedirectState = BTLocalStorage.get("bt-object-oauthRedirectState");
    if (!oauthRedirectState?.returnSearch) {
        return undefined;
    }
    const { returnSearch, ...rest } = oauthRedirectState;
    BTLocalStorage.set("bt-object-oauthRedirectState", rest);
    return returnSearch;
}
